import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  LuHouse,
  LuClipboardCheck,
  LuBanknote,
  LuShoppingCart,
  LuBoxes,
  LuMegaphone,
  LuSettings,
  LuLogOut,
} from "react-icons/lu";
import { useAuth } from "../lib/auth";
import { useActiveFlat } from "../lib/active-flat";
import { useNtfy } from "../lib/use-ntfy";
import { useEvents } from "../lib/use-events";
import { useToast } from "../lib/toast";
import { registerServiceWorker } from "../lib/pwa";
import { resubscribeIfMissing } from "../lib/push";
import { InstallHint, UpdateBanner } from "./PwaPrompts";
import { IconButton } from "./ui";

/** Fired on the window when live inventory changes; InventoryPage refetches. */
export const ITEMS_EVENT = "flat:items-changed";

const navItems = [
  { to: "/", label: "Home", icon: LuHouse, end: true },
  { to: "/chores", label: "Chores", icon: LuClipboardCheck },
  { to: "/expenses", label: "Expenses", icon: LuBanknote },
  { to: "/shopping", label: "Shopping", icon: LuShoppingCart },
  { to: "/inventory", label: "Inventory", icon: LuBoxes },
  { to: "/board", label: "Board", icon: LuMegaphone },
  { to: "/settings", label: "Settings", icon: LuSettings },
];

export function AppShell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { flat } = useActiveFlat();
  const navigate = useNavigate();
  const location = useLocation();

  const topic =
    auth.status === "authed" && flat
      ? `flat-${flat.id}-${auth.user.id}`
      : null;
  useNtfy(topic);

  const addToast = useToast();
  const [updateReady, setUpdateReady] = useState(false);
  const meId = auth.status === "authed" ? auth.user.id : null;

  // Register the service worker once, and self-heal a lost push subscription.
  useEffect(() => {
    registerServiceWorker(() => setUpdateReady(true));
    resubscribeIfMissing();
  }, []);

  // Push received while a window is visible → the SW forwards it here so we
  // show an in-app toast instead of a duplicate OS notification.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === "push") {
        const p = e.data.payload ?? {};
        addToast({ title: p.title, message: p.body ?? "" });
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [addToast]);

  // Live SSE stream: toasts for events aimed at me, live inventory for everyone.
  useEvents(auth.status === "authed", (event) => {
    if (event.type === "item") {
      window.dispatchEvent(new Event(ITEMS_EVENT));
    } else if (event.type === "notify" && event.userId === meId) {
      addToast({ title: event.title, message: event.message ?? "" });
    }
  });

  const handleLogout = async () => {
    await auth.logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-full flex-col pb-[calc(4rem+env(safe-area-inset-bottom))] sm:pb-0">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-brand-600 font-semibold text-white">
              {flat?.name?.[0]?.toUpperCase() ?? "F"}
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">{flat?.name ?? "Flat"}</div>
              <div className="text-xs text-slate-500">
                {flat?.members.length ?? 0} member{flat && flat.members.length === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {auth.status === "authed" && (
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-6 w-6 rounded-full"
                  style={{ background: auth.user.colour }}
                  aria-hidden
                />
                <span className="hidden text-sm text-slate-700 sm:inline">{auth.user.name}</span>
              </div>
            )}
            <IconButton
              icon={<LuLogOut className="h-4 w-4" />}
              label="Sign out"
              onClick={handleLogout}
            />
          </div>
        </div>
        {/* Desktop nav — hidden on mobile */}
        <nav className="mx-auto hidden max-w-6xl gap-1 px-2 pb-2 text-sm sm:flex">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition ${
                  isActive
                    ? "bg-brand-600 text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          className="mx-auto w-full max-w-6xl flex-1 px-4 py-6"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
        >
          <InstallHint />
          {children}
        </motion.main>
      </AnimatePresence>

      <UpdateBanner show={updateReady} />

      {/* Mobile bottom tab bar — clear the iOS home indicator, minus the padding
          the tab items already have so it doesn't look over-padded. */}
      <nav className="fixed inset-x-0 bottom-0 z-50 flex border-t border-slate-200 bg-white pb-[max(0px,calc(env(safe-area-inset-bottom)-0.5rem))] sm:hidden">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition ${
                isActive ? "text-brand-600" : "text-slate-400"
              }`
            }
          >
            {({ isActive }) => (
              <motion.span
                className="flex flex-col items-center gap-0.5"
                whileTap={{ scale: 0.85 }}
                animate={isActive ? { scale: 1.08 } : { scale: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
              >
                <Icon className="h-5 w-5" />
                {label}
              </motion.span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
